"use client";

import { Button, Heading, Text } from "@repo/components";
import type { ReactNode } from "react";
import { Box, Stack } from "@repo/styled-system/jsx";

/** A call-to-action: a label plus a click handler. */
export interface EmptyStateAction {
  /** Visible label for the primary action. */
  label: string;
  /** Invoked when the action is activated. */
  onClick: () => void;
}

export interface EmptyStateProps {
  /** Optional icon / illustration rendered above the title. */
  icon?: ReactNode;
  /** Headline describing the empty state. */
  title: string;
  /** Optional supporting copy under the title. */
  description?: string;
  /** Optional primary action. */
  action?: EmptyStateAction;
}

/**
 * Presentational empty state: an optional icon, a title, optional description and
 * an optional primary action. Takes all data + callbacks via typed props; the app
 * wires behaviour. No vendor SDKs, no integration imports.
 */
export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  return (
    <Stack
      align="center"
      textAlign="center"
      gap="4"
      px="6"
      py={{ base: "10", md: "16" }}
      maxW="md"
      mx="auto"
      color="fg.default"
    >
      {icon ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxSize="12"
          borderRadius="full"
          bg="bg.muted"
          color="fg.muted"
          fontSize="2xl"
        >
          {icon}
        </Box>
      ) : null}

      <Stack gap="1.5" align="center">
        <Heading as="h2" textStyle={{ base: "lg", md: "xl" }} color="fg.default">
          {title}
        </Heading>
        {description ? (
          <Text textStyle="sm" color="fg.muted" maxW="sm">
            {description}
          </Text>
        ) : null}
      </Stack>

      {action ? (
        <Button variant="solid" size="md" onClick={action.onClick} mt="2">
          {action.label}
        </Button>
      ) : null}
    </Stack>
  );
}
